import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './lib/auth';
import { Layout } from './components/Layout';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { Producao } from './pages/Producao';
import { Comercial } from './pages/Comercial';
import { Entregas } from './pages/Entregas';
import { DashComercial } from './pages/DashComercial';
import { DashProducao } from './pages/DashProducao';
import { PortalCliente } from './pages/PortalCliente';
import { PortalLayout } from './portal/PortalLayout';
import { HomePortal } from './portal/HomePortal';
import { EntregasPortal } from './portal/EntregasPortal';
import { EventosPortal } from './portal/EventosPortal';
import { EventoDetalhePortal } from './portal/EventoDetalhePortal';
import { GaleriaPortal } from './portal/GaleriaPortal';
import { OportunidadesPortal } from './portal/OportunidadesPortal';
import { AprovarPortal } from './portal/AprovarPortal';
import { ContatoPortal } from './portal/ContatoPortal';
import { Portais } from './pages/Portais';
import { Eventos } from './pages/Eventos';
import { EpisodiosMB } from './pages/EpisodiosMB';
import { Inscricoes } from './pages/Inscricoes';
import { Internos } from './pages/Internos';
import { Lembretes } from './pages/Lembretes';
import { AprovarPublico } from './pages/AprovarPublico';
import { MinhasDemandas } from './pages/MinhasDemandas';
import { Home } from './pages/Home';
import { TimeArea } from './pages/TimeArea';
import { NotFound } from './pages/NotFound';
import AuditLog from './pages/AuditLog';
import MacEnxoval from './pages/MacEnxoval';
import MacPlanejamento from './pages/MacPlanejamento';
import MacOfertas from './pages/MacOfertas';
import type { Papel } from './lib/types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function PrivateRoute({ roles }: { roles?: Papel[] }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted text-sm">Carregando…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.papel)) {
    return <Navigate to={defaultRoute(user.papel)} replace />;
  }
  return <Layout><Outlet /></Layout>;
}

function PrivateRouteNoLayout({ roles }: { roles?: Papel[] }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-muted text-sm">Carregando…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.papel)) {
    return <Navigate to={defaultRoute(user.papel)} replace />;
  }
  return <Outlet />;
}

function defaultRoute(papel: Papel): string {
  switch (papel) {
    case 'Cliente':    return '/portal';
    default:           return '/home';
  }
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={defaultRoute(user.papel)} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/aprovar/:token" element={<AprovarPublico />} />

            {/* ===== HOME ===== */}
            <Route element={<PrivateRoute roles={['Coordenação', 'Diretoria', 'Executor', 'Comercial']} />}>
              <Route path="/home" element={<Home />} />
            </Route>

            {/* ===== DEMANDAS (ex-Produção) ===== */}
            <Route element={<PrivateRoute roles={['Coordenação', 'Diretoria', 'Executor']} />}>
              <Route path="/demandas" element={<Producao />} />
              <Route path="/mundo-business" element={<EpisodiosMB />} />
              <Route path="/demandas/:area" element={<TimeArea />} />
              <Route path="/dash-producao" element={<DashProducao />} />
              <Route path="/minhas-demandas" element={<MinhasDemandas />} />
            </Route>

            {/* ===== MAC — Sprint 8 ===== */}
            <Route element={<PrivateRoute roles={['Coordenação', 'Diretoria', 'Executor']} />}>
              <Route path="/mac/enxoval" element={<MacEnxoval />} />
              <Route path="/mac/planejamento" element={<MacPlanejamento />} />
              <Route path="/mac/ofertas" element={<MacOfertas />} />
            </Route>

            {/* ===== PLATAFORMA (ex-Comercial) + Crescimento + Dash Plataforma ===== */}
            <Route element={<PrivateRoute roles={['Coordenação', 'Diretoria', 'Comercial']} />}>
              <Route path="/comercial" element={<Comercial />} />
              <Route path="/dash-comercial" element={<DashComercial />} />
              <Route path="/internos" element={<Internos />} />
            </Route>

            {/* Inscrições: acessível por Comercial + staff + Executor (time Eventos) */}
            <Route element={<PrivateRoute roles={['Coordenação', 'Diretoria', 'Comercial', 'Executor']} />}>
              <Route path="/inscricoes" element={<Inscricoes />} />
            </Route>

            <Route element={<PrivateRoute roles={['Coordenação', 'Diretoria', 'Comercial', 'Executor']} />}>
              <Route path="/entregas" element={<Entregas />} />
              <Route path="/eventos" element={<Eventos />} />
              <Route path="/lembretes" element={<Lembretes />} />
            </Route>

            <Route element={<PrivateRoute roles={['Coordenação', 'Diretoria', 'Comercial']} />}>
              <Route path="/portais" element={<Portais />} />
            </Route>

            {/* ===== AUDIT LOG (Coordenação/Diretoria) ===== */}
            <Route element={<PrivateRoute roles={['Coordenação', 'Diretoria']} />}>
              <Route path="/audit" element={<AuditLog />} />
            </Route>

            <Route element={<PrivateRouteNoLayout />}>
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<HomePortal />} />
                <Route path="aprovar" element={<AprovarPortal />} />
                <Route path="entregas" element={<EntregasPortal />} />
                <Route path="eventos" element={<EventosPortal />} />
                <Route path="eventos/:id" element={<EventoDetalhePortal />} />
                <Route path="galeria" element={<GaleriaPortal />} />
                <Route path="oportunidades" element={<OportunidadesPortal />} />
                <Route path="contato" element={<ContatoPortal />} />
              </Route>
              <Route path="/portal-legado" element={<PortalCliente />} />
            </Route>

            {/* ===== Aliases / redirects pra compatibilidade ===== */}
            <Route path="/coordenacao"     element={<Navigate to="/demandas" replace />} />
            <Route path="/producao"        element={<Navigate to="/demandas" replace />} />
            <Route path="/time/:area"      element={<RedirectTimeToDemandas />} />
            <Route path="/diretoria"       element={<Navigate to="/dash-comercial" replace />} />
            <Route path="/metricas"        element={<Navigate to="/dash-comercial" replace />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Redirect: /time/<area> → /demandas/<area>
import { useParams } from 'react-router-dom';
function RedirectTimeToDemandas() {
  const { area } = useParams<{ area: string }>();
  return <Navigate to={`/demandas/${area ?? ''}`} replace />;
}
